import { z } from 'zod'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { parseEther, formatEther } from 'viem'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { zodResolver } from '@hookform/resolvers/zod'
import { DialogProps } from '@radix-ui/react-dialog'
import { useWritePepperContract } from '@/hooks/pepper-contract/useWritePepperContract'
import { useStaking2aAddress } from '@/hooks/staking2a-contract/useStaking2aContract'
import { useWriteStaking2aContract } from '@/hooks/staking2a-contract/useWriteStaking2aContract'
import { usePepperContractAddress } from '@/hooks/pepper-contract/usePepperContract'
import { waitForTransactionReceipt } from '@/utils/wagmi/waitForTransactionReceipt'
import { usePepperBalance } from '@/hooks/pepper-contract/usePepperBalance'
import { usePepperUserAllowanceOnStaking } from '@/hooks/pepper-contract/usePepperUserAllowanceOnStaking'
import { useCurrencyFormatter } from '@/hooks/helpers/useCurrencyFormatter'
import { AmountForm } from './AmountForm'
import { logger } from '@/observability/logger'
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle
} from '@/components/ui/ResponsiveDialog'
import { testIdFactory } from '@/utils/test-id-factory.ts'

const stakingFormSchema = (balance: bigint) =>
	z.object({
		amount: z
			.string()
			.regex(/^\d*\.?\d*$/, 'Invalid number')
			.refine(x => Number(x) >= 1, 'Must be at least 1 PEPPER')
			.refine(x => Number(x) <= Number(formatEther(balance)), 'Amount exceeds your PEPPER balance')
	})

export function StakeDialog({ onSubmit, ...dialogProps }: DialogProps & { onSubmit: () => void }) {
	// Contract Hooks
	const writePepperContract = useWritePepperContract()
	const writeStaking2aContract = useWriteStaking2aContract()
	const stakingAddress = useStaking2aAddress()
	const pepperAddress = usePepperContractAddress()

	// Data Hooks
	const { data: pepperBalance = 0n, isPending: isPendingPepperBalance } = usePepperBalance()
	const {
		data: pepperAllowance = 0n,
		isPending: isPendingPepperAllowance,
		refetch: refetchAllowance
	} = usePepperUserAllowanceOnStaking()
	const format = useCurrencyFormatter(3)

	// Form State
	const [isStakingAllowed, setIsStakingAllowed] = useState(false)
	const form = useForm({
		mode: 'onChange',
		resolver: zodResolver(stakingFormSchema(pepperBalance))
	})

	const amount = useWatch({ control: form.control, name: 'amount' })
	const amountInWei = amount ? parseEther(amount) : 0n
	const isLoading = isPendingPepperBalance || isPendingPepperAllowance

	// Reset approval when amount changes
	useEffect(() => {
		if (isStakingAllowed) {
			setIsStakingAllowed(false)
			refetchAllowance()
		}
	}, [amountInWei])

	// Set default amount to Pepper balance
	useEffect(() => {
		const defaultValue = formatEther(pepperBalance) ?? '0'
		form.setValue('amount', defaultValue, {
			shouldValidate: true
		})
	}, [pepperBalance])

	const isPepperAllowanceApproved = (pepperAllowance: bigint, amountToStake: bigint): boolean =>
		pepperAllowance >= amountToStake

	const handlePepperAllowance = async (): Promise<void> => {
		try {
			const hash = await writePepperContract({
				functionName: 'approve',
				args: [stakingAddress, amountInWei]
			})

			await waitForTransactionReceipt({ hash })
			setIsStakingAllowed(true)
			/* TODO(DFT-1951): Remove this line form.setValue is it seems unncecessary
			 * keeping it in case we encounter any issues with the form state
			 */
			// form.setValue('amount', amount, {
			// 	shouldValidate: true
			// })
		} catch (e) {
			logger.error(e, 'Error approving Pepper allowance')
			toast.error('An error occurred while approving Pepper allowance.')
		}
	}

	const handleApprove = form.handleSubmit(async () => {
		const isApproved = isPepperAllowanceApproved(pepperAllowance, amountInWei)
		if (isApproved) {
			setIsStakingAllowed(true)

			return
		}
		await handlePepperAllowance()
	})

	const handleConfirm = form.handleSubmit(async () => {
		try {
			const hash = await writeStaking2aContract({
				functionName: 'stake',
				args: [amountInWei, pepperAddress]
			})
			await waitForTransactionReceipt({ hash })
			setIsStakingAllowed(false)
			onSubmit()
		} catch (e) {
			logger.error(e, 'Error staking Pepper')
			toast.error('An error occurred while staking Pepper.')
		}
	})

	const submitButtonLabel = isStakingAllowed ? 'stake' : 'approve'

	return (
		<ResponsiveDialog {...dialogProps}>
			<ResponsiveDialogContent>
				<div className="overflow-y-auto gap-8 flex flex-col">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>How much Pepper do you want to stake?</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>
					<div>
						<AmountForm form={form} base={pepperBalance ?? 0n} disabled={isStakingAllowed} key="stake" />
					</div>
					<h4 className="text-center text-3xl text-neutral-40 leading-none flex items-center justify-center gap-2 flex-col md:flex-row">
						<span>PEPPER Balance:</span>
						{isLoading ? (
							<Skeleton className="w-[13ch] h-[30px]" />
						) : (
							<span>{format(formatEther(pepperBalance ?? 0n))}</span>
						)}
					</h4>
					<ResponsiveDialogFooter>
						<Button
							onClick={isStakingAllowed ? handleConfirm : handleApprove}
							disabled={!form.formState.isValid || !amount}
							loading={form.formState.isSubmitting}
							variant="danger"
							className="py-5 px-10 leading-none capitalize"
							data-testId={testIdFactory.pepperAmountFormButton(submitButtonLabel)}
						>
							{submitButtonLabel}
						</Button>
					</ResponsiveDialogFooter>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	)
}
